import { Fullscreen, FullscreenExit, Settings } from "@mui/icons-material";
import { Box, capitalize, IconButton, Stack, Tooltip, Typography } from "@mui/material";
import {
  Controls,
  Handle,
  MarkerType,
  MiniMap,
  type NodeProps,
  Position,
  ReactFlow,
  ReactFlowProvider,
  useReactFlow,
} from "@xyflow/react";
import { forceCenter, forceCollide, forceLink, forceManyBody, forceSimulation } from "d3-force";
import dagre from "dagre";
import type React from "react";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router";
import type { AppConfig, GraphEdge, GraphNode, Test } from "../types.ts";
import { clickableProps } from "../utils/keyboard";

import "@xyflow/react/dist/style.css";

// Graph layout using Dagre
const calculateNodePositions = (
  nodes: GraphNode[],
  edges: GraphEdge[],
  horizontal: boolean = false,
  layoutConfig?: LayoutConfig,
) => {
  const dagreGraph = new dagre.graphlib.Graph();
  dagreGraph.setDefaultEdgeLabel(() => ({}));

  // Default layout settings
  const defaultRankdir = horizontal ? "LR" : "TB";
  const defaultNodesep = horizontal ? 100 : 80;
  const defaultRanksep = horizontal ? 200 : 150;

  // Merge with user-provided config
  dagreGraph.setGraph({
    rankdir: layoutConfig?.rankdir ?? defaultRankdir, // Left-to-right for horizontal, Top-to-bottom for vertical
    nodesep: layoutConfig?.nodesep ?? defaultNodesep, // Spacing between nodes in the same rank
    ranksep: layoutConfig?.ranksep ?? defaultRanksep, // Spacing between ranks
    marginx: 50,
    marginy: 50,
  });

  const nodeWidth = 200;
  const nodeHeight = 100;

  // Add nodes to dagre
  nodes.forEach((node) => {
    dagreGraph.setNode(node.id, { width: nodeWidth, height: nodeHeight });
  });

  // Add edges to dagre
  edges.forEach((edge) => {
    dagreGraph.setEdge(edge.source, edge.target);
  });

  // Calculate layout
  dagre.layout(dagreGraph);

  // Extract positions
  const positions: { [key: string]: { x: number; y: number } } = {};
  nodes.forEach((node) => {
    const nodeWithPosition = dagreGraph.node(node.id);
    positions[node.id] = {
      x: nodeWithPosition.x - nodeWidth / 2,
      y: nodeWithPosition.y - nodeHeight / 2,
    };
  });

  return positions;
};

// Force-directed layout using D3
const calculateForceDirectedPositions = (nodes: GraphNode[], edges: GraphEdge[]) => {
  if (nodes.length === 0) {
    return {};
  }

  // Create node objects with ids for d3-force
  // Initialize with random positions to help the simulation
  const d3Nodes = nodes.map((node, i) => ({
    id: node.id,
    x: Math.cos((i * 2 * Math.PI) / nodes.length) * 100,
    y: Math.sin((i * 2 * Math.PI) / nodes.length) * 100,
  }));

  // Create link objects for d3-force
  const d3Links = edges.map((edge) => ({
    source: edge.source,
    target: edge.target,
  }));

  // Run force simulation
  const simulation = forceSimulation(d3Nodes)
    .force(
      "link",
      forceLink(d3Links)
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        .id((d: any) => d.id)
        .distance(120)
        .strength(0.3),
    )
    .force("charge", forceManyBody().strength(-400))
    .force("center", forceCenter(0, 0))
    .force("collide", forceCollide(100).strength(0.9));

  // Run simulation synchronously for more iterations
  simulation.stop();
  for (let i = 0; i < 500; i++) {
    simulation.tick();
  }

  // Extract final positions
  const positions: { [key: string]: { x: number; y: number } } = {};
  d3Nodes.forEach((node) => {
    positions[node.id] = {
      x: node.x ?? 0,
      y: node.y ?? 0,
    };
  });

  // Debug: Log sample positions to verify they're calculated
  if (d3Nodes.length > 0) {
    console.log(
      "Force layout calculated positions for",
      d3Nodes.length,
      "nodes. Sample:",
      positions[d3Nodes[0].id],
    );
  }

  return positions;
};

interface LayoutConfig {
  rankdir?: "TB" | "LR";
  nodesep?: number;
  ranksep?: number;
}

interface GraphVisualizationProps {
  nodes: GraphNode[];
  edges: GraphEdge[];
  path?: GraphEdge[];
  horizontal?: boolean;
  onEditGraph?: () => void;
  tests?: Test[];
  appConfigs?: AppConfig[];
  showMinimap?: boolean;
  enableFullscreen?: boolean;
  responsiveHeight?: boolean;
  layoutConfig?: LayoutConfig;
  layoutType?: "hierarchical" | "force";
}

const KnowledgeNode = ({ data, isConnectable }: NodeProps) => {
  const navigate = useNavigate();
  const {
    type,
    label,
    description,
    content_type,
    interaction_type,
    app_config_name,
    app_config_base_url,
    horizontal,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } = data as any as GraphNode & {
    label: string;
    app_config_name?: string;
    app_config_base_url?: string;
    horizontal?: boolean;
  };

  let borderColor = "divider";
  let borderStyle: "solid" | "dashed" | "dotted" = "solid";
  const backgroundColor = "background.paper";

  switch (type as string) {
    case "page":
      borderColor = "primary.main";
      break;
    case "interaction":
      borderColor = "secondary.main";
      break;
    case "content":
      borderColor = "success.main";
      break;
    case "dependency":
      borderColor = "grey.400";
      borderStyle = "dotted";
      break;
    case "pinnedVersion":
      borderColor = "primary.main";
      break;
    default:
      borderColor = "divider";
  }

  if ((type as string).startsWith("test")) {
    borderColor = "success.main";
  }

  // Check if this is a test node or dependency node that should be clickable
  const isTestNode = (type as string).startsWith("test");
  const isDependencyNode = (type as string) === "dependency";
  const isClickableNode = isTestNode || isDependencyNode;
  const testId = isClickableNode ? data.id : null;

  const handleNodeClick = () => {
    if (isClickableNode && testId) {
      navigate(`/tests/${testId}`);
    }
  };

  return (
    <Box
      alignItems="center"
      justifyContent="center"
      border={2}
      bgcolor={backgroundColor}
      borderColor={borderColor}
      borderRadius={2}
      p={1}
      minWidth={180}
      {...clickableProps(handleNodeClick)}
      onClick={handleNodeClick}
      sx={{
        borderStyle: borderStyle,
        cursor: isClickableNode ? "pointer" : "default",
        "&:hover": isClickableNode
          ? {
              boxShadow: 2,
              transform: "translateY(-1px)",
              transition: "all 0.2s ease-in-out",
            }
          : {},
      }}
    >
      <Handle
        type="target"
        position={horizontal ? Position.Left : Position.Top}
        isConnectable={isConnectable}
      />
      <Stack direction="column" alignItems="center" spacing={0}>
        <Typography variant="caption" color="text.secondary" fontSize={8}>
          {capitalize(type.replace(/_/g, " "))}
        </Typography>
        <Typography
          variant="body1"
          fontWeight="bold"
          align="center"
          fontSize={12}
          maxWidth={160}
          noWrap
        >
          {label}
        </Typography>
        {app_config_name && (
          <Box sx={{ mt: 0.25, textAlign: "center" }}>
            <Typography
              variant="caption"
              fontWeight="medium"
              fontSize={9}
              maxWidth={160}
              noWrap
              sx={{ display: "block" }}
            >
              {app_config_name}
            </Typography>
            {app_config_base_url && (
              <Typography
                variant="caption"
                color="text.secondary"
                fontSize={8}
                maxWidth={160}
                noWrap
                sx={{ display: "block" }}
              >
                {app_config_base_url}
              </Typography>
            )}
          </Box>
        )}
        {content_type && (
          <Typography variant="caption" fontSize={10}>
            <code>{content_type}</code>
          </Typography>
        )}
        {interaction_type && (
          <Typography variant="caption" fontSize={10}>
            <code>{interaction_type}</code>
          </Typography>
        )}
        {description && (
          <Typography
            variant="body2"
            color="text.secondary"
            fontSize={10}
            maxWidth={140}
            textAlign="center"
            noWrap
          >
            {description}
          </Typography>
        )}
      </Stack>
      <Handle
        type="source"
        position={horizontal ? Position.Right : Position.Bottom}
        isConnectable={isConnectable}
      />
    </Box>
  );
};

const nodeTypes = {
  page: KnowledgeNode,
  interaction: KnowledgeNode,
  content: KnowledgeNode,
  test: KnowledgeNode,
  test_target: KnowledgeNode,
  dependency: KnowledgeNode,
  version: KnowledgeNode,
  pinnedVersion: KnowledgeNode,
};

const GraphVisualizationInner: React.FC<GraphVisualizationProps> = ({
  nodes,
  edges,
  path,
  horizontal = false,
  onEditGraph,
  tests,
  appConfigs,
  showMinimap = false,
  enableFullscreen = false,
  responsiveHeight = false,
  layoutConfig,
  layoutType = "hierarchical",
}) => {
  const [isFullscreen, setIsFullscreen] = useState(false);
  const { fitView } = useReactFlow();

  const graphPositions = useMemo(() => {
    if (layoutType === "force") {
      return calculateForceDirectedPositions(nodes, edges);
    }
    return calculateNodePositions(nodes, edges, horizontal, layoutConfig);
  }, [nodes, edges, horizontal, layoutConfig, layoutType]);

  // Trigger fitView when component mounts or when critical props change
  useEffect(() => {
    // Longer delay for force layout to ensure positions are calculated
    const delay = layoutType === "force" ? 300 : 100;
    const timer = setTimeout(() => {
      fitView({
        padding: 0.2,
        duration: 400,
        maxZoom: 1.5,
        minZoom: 0.1,
      });
    }, delay);
    return () => clearTimeout(timer);
  }, [fitView, layoutType]);

  const flowNodes = useMemo(
    () =>
      nodes.map((node) => {
        // Look up test and app config for dependency/test nodes
        let appConfigName: string | undefined;
        let appConfigBaseUrl: string | undefined;
        if ((node.type === "dependency" || node.type.startsWith("test")) && tests && appConfigs) {
          const test = tests.find((t) => t.id === node.id);
          if (test?.app_config_id) {
            const appConfig = appConfigs.find((c) => c.id === test.app_config_id);
            if (appConfig) {
              appConfigName = appConfig.name;
              appConfigBaseUrl = appConfig.base_url ?? "about:blank";
            }
          }
        }

        const position = graphPositions[node.id];
        return {
          id: node.id,
          type: node.type,
          position: {
            x: position?.x ?? 0,
            y: position?.y ?? 0,
          },
          data: {
            label: node.name,
            ...node,
            app_config_name: appConfigName,
            app_config_base_url: appConfigBaseUrl,
            horizontal,
          },
        };
      }),
    [nodes, graphPositions, tests, appConfigs, horizontal],
  );

  const flowEdges = useMemo(
    () =>
      edges.map((edge) => {
        const isPathEdge = path?.some((p) => p.source === edge.source && p.target === edge.target);

        return {
          type: "bezier",
          id: `${edge.source}-${edge.target}`,
          source: edge.source,
          target: edge.target,
          animated: isPathEdge,
          markerEnd: {
            type: MarkerType.Arrow,
            color: isPathEdge ? "green" : "#ccc",
          },
          style: {
            stroke: isPathEdge ? "green" : "#ccc",
            strokeWidth: isPathEdge ? 3 : 1,
          },
        };
      }),
    [edges, path],
  );

  // Determine height based on fullscreen and responsiveHeight props
  const containerHeight = isFullscreen
    ? "100vh"
    : responsiveHeight
      ? "calc(100vh - 250px)"
      : "600px";

  return (
    <Box
      width="100%"
      height={containerHeight}
      border={1}
      margin={isFullscreen ? 0 : "8px"}
      borderColor="divider"
      borderRadius={isFullscreen ? 0 : 2}
      sx={{
        ...(isFullscreen && {
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 9999,
          backgroundColor: "background.default",
        }),
      }}
    >
      <ReactFlow
        key={`graph-${layoutType}-${nodes.length}`}
        nodes={flowNodes}
        edges={flowEdges}
        minZoom={0.1}
        maxZoom={4}
        nodeTypes={nodeTypes}
        proOptions={{ hideAttribution: true }}
      >
        <Controls showZoom showFitView showInteractive />
        {showMinimap && (
          <MiniMap
            zoomable
            pannable
            style={{
              backgroundColor: "rgba(0, 0, 0, 0.05)",
            }}
          />
        )}
        {(onEditGraph || enableFullscreen) && (
          <Box
            sx={{
              position: "absolute",
              top: 10,
              right: 10,
              zIndex: 1000,
              display: "flex",
              gap: 1,
            }}
          >
            {enableFullscreen && (
              <Tooltip title={isFullscreen ? "Exit Fullscreen" : "Fullscreen"} placement="left">
                <IconButton
                  aria-label={isFullscreen ? "Exit Fullscreen" : "Fullscreen"}
                  size="small"
                  sx={{
                    backgroundColor: "background.paper",
                    border: 1,
                    borderColor: "divider",
                    "&:hover": {
                      backgroundColor: "action.hover",
                    },
                  }}
                  onClick={() => setIsFullscreen(!isFullscreen)}
                >
                  {isFullscreen ? (
                    <FullscreenExit fontSize="small" />
                  ) : (
                    <Fullscreen fontSize="small" />
                  )}
                </IconButton>
              </Tooltip>
            )}
            {onEditGraph && (
              <Tooltip title="Change Dependencies" placement="left">
                <IconButton
                  aria-label="Change Dependencies"
                  size="small"
                  sx={{
                    backgroundColor: "background.paper",
                    border: 1,
                    borderColor: "divider",
                    "&:hover": {
                      backgroundColor: "action.hover",
                    },
                  }}
                  onClick={onEditGraph}
                >
                  <Settings fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        )}
      </ReactFlow>
    </Box>
  );
};

// Wrapper component to provide ReactFlow context
const GraphVisualization: React.FC<GraphVisualizationProps> = (props) => {
  return (
    <ReactFlowProvider>
      <GraphVisualizationInner {...props} />
    </ReactFlowProvider>
  );
};

export default GraphVisualization;
